import { useEffect, useRef, useState } from "react";
import { View, Text, Pressable, TextInput, ScrollView } from "react-native";
import {
  ActionRowCard,
  Dialog,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  DialogTitleText,
  SoftSecondaryButton,
  colors,
} from "@louise/moon-ui";
import {
  AlertTriangle,
  Calendar as CalendarIcon,
  CheckCircle2,
  ChevronLeft,
  CircleAlert,
  ClipboardList,
  HandHeart,
  Heart,
} from "lucide-react-native";
import {
  useActiveCycle,
  useCompleteCycle,
  useUpdateCycle,
} from "@src/packages/cycles";
import {
  CANCELLATION_REASON_KEYS,
  type CycleType,
  type CancellationReason,
  type CycleOutcome,
} from "@src/packages/cycles/hooks/useCycles";
import { buildEndCycleOutcome } from "./endCycleOutcomeMerge";
import {
  outcomeTypeForOption,
  resolveEndCycleFamily,
  resolveEndCycleOptions,
  type EndCycleContext,
  type EndCycleOption,
  type EndCycleOptionId,
} from "./endCycleOptions";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useLocale } from "@src/packages/i18n/useLocale";
import endingIllustration from "../../../../assets/images/end-treatment-cycle-illustration.png";
import { NegativeCloseoutSupport } from "../../care/modals/NegativeCloseoutSupport";

/**
 * What the patient can pick. Wider than the API's outcome types because
 * `completed` — how a preservation or donation cycle ends — is stored as a null
 * outcome type. See `outcomeTypeForOption`.
 */
type OutcomeType = EndCycleOptionId;

type Step = "pick" | "details" | "confirmation";

type Props = {
  visible: boolean;
  onClose: () => void;
  /** Optional: target a specific cycle. Defaults to the active cycle. */
  cycleId?: string;
  /** Optional: existing outcome id for upsert. */
  outcomeId?: string | null;
  /**
   * Optional: the ending already recorded on the target cycle.
   *
   * Needed because a PATCH replaces the whole outcome sub-object, so this modal
   * has to send back the fields it does not own or they are destroyed. Callers
   * that target a specific cycle should pass its outcome; otherwise the active
   * cycle's is used.
   */
  existingOutcome?: CycleOutcome | null;
  /** Optional: run before the cycle is updated (e.g., to snapshot history). */
  onBeforeSave?: () => Promise<void>;
  /**
   * Optional: called after the user taps Done on the confirmation step.
   *
   * Receives the ending that was actually saved. A caller that opened this modal
   * expecting one ending must not assume it got it — the picker is contextual to
   * the cycle's family, phase, and any result already recorded.
   */
  onSaved?: (savedOutcomeType: OutcomeType) => void;
  /** Optional: transfer/insemination date. Enables the pregnancy timeline. */
  transferDate?: string | null;
  /**
   * Whether a completed transfer or insemination makes a pregnancy outcome
   * meaningful for this current-cycle close. Omit for callers without phase
   * data; the modal falls back to the transfer date or recorded result.
   */
  hasPregnancyOpportunity?: boolean;
  /** Explicitly distinguish current closeout from historical outcome entry. */
  context?: EndCycleContext;
  /** Optional recap for preservation cycles completed from Finish Treatment. */
  preservationSummary?: PreservationCycleSummary;
  /** The target cycle type, used when closing preservation cycles elsewhere. */
  preservationCycleType?: CycleType | null;
  /**
   * Optional: navigate the reader to a suggested article. When provided, the
   * negative-outcome confirmation surfaces a small reading rail.
   */
  onOpenArticle?: (articleId: string) => void;
};

export type PreservationOutcomeCounts = {
  kind: "eggs" | "embryos" | "sperm";
  retrieved: number | null;
  mature: number | null;
  fertilized: number | null;
  frozen: number | null;
};

export type PreservationSpecimenSummary = {
  type: "oocyte" | "embryo" | "semen";
  count: number;
  facility?: string;
};

export type PreservationCycleSummary = {
  treatmentName: string;
  clinicName: string | null;
  startDate: string | null;
  cycleNumber: number;
  outcomeCounts: PreservationOutcomeCounts;
  specimens: PreservationSpecimenSummary[];
  onAddSpecimen?: () => void;
};

/**
 * One illustration for every ending.
 *
 * The app's dialogs carry a 3D illustration above the title — this modal was
 * drawing a lucide glyph in a tinted circle instead, a treatment used nowhere
 * else. A single neutral illustration also keeps tone in the copy where the rest
 * of this flow keeps it: art that celebrated would celebrate a loss too.
 */
const ENDING_ILLUSTRATION = endingIllustration;

const CANCELLATION_REASON_ORDER: CancellationReason[] = [
  "medical_advice",
  "personal_choice",
  "side_effects",
  "protocol_switch",
  "financial",
  "trigger_not_administered",
  "no_retrieval",
  "other",
];

function expectedStoredSpecimenType(
  kind: PreservationOutcomeCounts["kind"],
): PreservationSpecimenSummary["type"] {
  if (kind === "embryos") return "embryo";
  if (kind === "sperm") return "semen";
  return "oocyte";
}

function getStoredSpecimenCount(
  summary: PreservationCycleSummary,
  type?: PreservationSpecimenSummary["type"],
): number {
  return summary.specimens.reduce((sum, specimen) => {
    return sum + (type == null || specimen.type === type ? specimen.count : 0);
  }, 0);
}

function hasStoredSpecimenMismatch(summary: PreservationCycleSummary): boolean {
  const expectedCount = summary.outcomeCounts.frozen;
  if (summary.outcomeCounts.kind === "sperm") {
    // Sperm closeout must reconcile the reported vial count with current-cycle
    // inventory. Null means the outcome has not been recorded.
    return (
      expectedCount == null ||
      getStoredSpecimenCount(
        summary,
        expectedStoredSpecimenType(summary.outcomeCounts.kind),
      ) !== expectedCount
    );
  }
  if (expectedCount == null || expectedCount <= 0) return false;
  return (
    getStoredSpecimenCount(
      summary,
      expectedStoredSpecimenType(summary.outcomeCounts.kind),
    ) !== expectedCount
  );
}

export default function EndCycleModal({
  visible,
  onClose,
  cycleId,
  outcomeId,
  existingOutcome,
  onBeforeSave,
  onSaved,
  transferDate,
  hasPregnancyOpportunity: hasPregnancyOpportunityProp,
  context = "current_cycle_close",
  preservationSummary,
  preservationCycleType,
  onOpenArticle,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("EndCycleModal");
  const { data: activeCycle } = useActiveCycle();
  const updateCycle = useUpdateCycle();
  const completeCycle = useCompleteCycle();
  const [step, setStep] = useState<Step>("pick");
  const [outcomeType, setOutcomeType] = useState<OutcomeType | null>(null);
  const [cancellationReason, setCancellationReason] =
    useState<CancellationReason | null>(null);
  const [notes, setNotes] = useState("");

  /**
   * Which endings this cycle can have. A preservation or donation cycle cannot
   * end in a pregnancy, and until now had no way to say it simply finished — so
   * completing egg freezing had to be filed as a result.
   */
  const authorizedExperience = useAuthorizedExperienceProjection();
  const targetCycleId = cycleId ?? activeCycle?.id;
  const targetOutcomeId =
    outcomeId !== undefined ? outcomeId : (activeCycle?.outcome?.id ?? null);
  const isPreservation =
    preservationSummary != null ||
    preservationCycleType === "egg_freezing" ||
    preservationCycleType === "embryo_freezing" ||
    preservationCycleType === "sperm_freezing";
  const treatmentGoal =
    authorizedExperience.status === "ready"
      ? (authorizedExperience.projection?.context.familyBuildingPlan
          .treatmentGoal ?? "unknown")
      : "unknown";
  const endCycleFamily = resolveEndCycleFamily({
    cycleType: preservationCycleType ?? activeCycle?.cycleType,
    treatmentGoal,
  });

  /**
   * Preselect the ending that is already recorded, if any.
   *
   * A cycle can reach this modal with its outcome already saved — recording a
   * beta result no longer closes the cycle, so the patient may be ending a cycle
   * whose result is known. Starting on an empty selection would ask them to
   * state it a second time, and saving replaces the whole outcome sub-object, so
   * a different pick would silently overwrite what was recorded.
   */
  // Prefer the caller's outcome: when a `cycleId` is passed the target may not be
  // the active cycle, and preserving another cycle's fields onto it would be
  // worse than preserving nothing.
  const recordedOutcome =
    existingOutcome !== undefined
      ? existingOutcome
      : (activeCycle?.outcome ?? null);
  const recordedOutcomeType = recordedOutcome?.type ?? null;
  const hasRecordedOutcome = recordedOutcome != null;
  const recordedCancellationReason =
    recordedOutcome?.cancellationReason ?? null;
  const hasPregnancyOpportunity =
    hasPregnancyOpportunityProp ??
    (transferDate != null ||
      recordedOutcomeType === "pregnancy" ||
      recordedOutcomeType === "no_pregnancy" ||
      recordedOutcomeType === "miscarriage");
  const { options: resolvedOutcomeOptions, promptKey } = resolveEndCycleOptions(
    {
      family: endCycleFamily,
      context,
      hasPregnancyOpportunity,
      recordedOutcomeType,
      hasRecordedOutcome,
    },
  );
  const outcomeOptions = resolvedOutcomeOptions;
  const showRecordedResultRecap =
    context === "current_cycle_close" &&
    !preservationSummary &&
    hasRecordedOutcome &&
    recordedOutcomeType != null;
  useEffect(() => {
    if (!visible) return;
    if (outcomeType) return;
    if (!recordedOutcomeType) return;
    setOutcomeType(recordedOutcomeType);
    if (
      recordedOutcomeType === "cancelled" &&
      recordedCancellationReason &&
      recordedCancellationReason in CANCELLATION_REASON_KEYS
    ) {
      setCancellationReason(recordedCancellationReason as CancellationReason);
    }
  }, [visible, outcomeType, recordedOutcomeType, recordedCancellationReason]);

  /**
   * True from a successful write until the confirmation it leads to is dismissed.
   *
   * The window the reset below has to keep its hands off; see the comment there.
   */
  const justSaved = useRef(false);

  /**
   * The transfer date as it stood when the save went out.
   *
   * The confirmation step draws its pregnancy timeline — first scan, end of the
   * first trimester, halfway — from `transferDate`, and every caller derives that
   * from the cycle's phases. Closing invalidates the cycle, so the phases re-key to
   * nothing and the prop goes null a moment after the step appears: the timeline
   * showed up and then vanished while she was reading it. Held here, the step keeps
   * the dates the cycle had when she closed it.
   */
  const [savedTransferDate, setSavedTransferDate] = useState<string | null>(
    null,
  );

  const reset = () => {
    setStep("pick");
    setOutcomeType(null);
    setCancellationReason(null);
    setNotes("");
    setSavedTransferDate(null);
    justSaved.current = false;
  };

  // Reset when the target cycle changes. Native Stack reuses this component
  // when the CycleDetail route swaps cycleId (e.g. after creating a new
  // cycle from CryoVault), so without this reset a leftover
  // `step === "confirmation"` from the previous cycle would flash on the
  // new one.
  //
  // What it must not do is undo the save's own last step. Closing invalidates
  // `active-cycle`, callers pass that cycle's id, so the id this modal was given
  // drops the moment the write lands — and resetting there sent the modal back to
  // "pick" with the outcome cleared. The recap could not render either, the closed
  // cycle's record no longer being readable, so the patient who had just confirmed
  // her result was handed a fresh "how do you want to close this cycle?" for a
  // cycle that was already closed, on a step that could do nothing: with no cycle
  // `targetCycleId` is undefined and `handleSave` returns immediately.
  //
  // The signal is the save, not the shape of the new id. Keying on "the id
  // disappeared" also skipped the reset when a specific cycle gave way to the
  // default active one with none open — a genuine target change, and one this
  // effect was written for — leaving another cycle's step and notes on screen with
  // no way to clear them. `justSaved` is only ever true between a successful write
  // and the confirmation being dismissed, which is exactly the window to protect.
  useEffect(() => {
    if (justSaved.current) return;
    setStep("pick");
    setOutcomeType(null);
    setCancellationReason(null);
    setNotes("");
    // Cleared here too, or a genuine target change reset everything except the
    // previous cycle's transfer date and left it to be read on the next one.
    setSavedTransferDate(null);
  }, [cycleId]);

  /**
   * Hiding the modal ends the window `justSaved` protects, however it was hidden.
   *
   * `reset()` was the only thing clearing it, and `reset()` only runs from paths
   * that go through this modal — so a parent flipping `visible` off on its own
   * (`CycleDetailScreen`'s "add specimen" does exactly that) left the flag true
   * for the rest of the mount, and the reset above never fired again. That is the
   * leftover-confirmation bug its own comment describes, reinstated. Keyed on
   * `visible`, the invariant holds without anyone having to remember it.
   */
  useEffect(() => {
    if (visible) return;
    justSaved.current = false;
  }, [visible]);

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleDone = () => {
    // Captured before `reset()` clears it, and only reported when something was
    // actually saved — the confirmation step is unreachable otherwise.
    const savedOutcomeType = outcomeType;
    onClose();
    reset();
    if (savedOutcomeType) onSaved?.(savedOutcomeType);
  };

  const handleSelectOutcome = (nextOutcomeType: OutcomeType) => {
    setOutcomeType(nextOutcomeType);
    if (nextOutcomeType !== "cancelled") setCancellationReason(null);
    setStep("details");
  };

  const handleBack = () => {
    setStep("pick");
  };

  const handleSave = async () => {
    if (!targetCycleId || (!preservationSummary && !outcomeType)) return;
    const today = new Date().toISOString().slice(0, 10);

    try {
      await onBeforeSave?.();
      if (preservationSummary) {
        await completeCycle.mutateAsync({
          cycleId: targetCycleId,
          notes: notes.trim() || undefined,
          outcomeId: targetOutcomeId ?? undefined,
        });
        onClose();
        reset();
        onSaved?.("completed");
        return;
      }
      if (!outcomeType) return;
      await updateCycle.mutateAsync({
        cycleId: targetCycleId,
        data: {
          // Preserves the fields this modal does not own; see
          // `buildEndCycleOutcome`.
          outcome: {
            ...(targetOutcomeId ? { id: targetOutcomeId } : {}),
            ...buildEndCycleOutcome({
              // `completed` stores a null type; see `outcomeTypeForOption`.
              outcomeType: outcomeTypeForOption(outcomeType),
              cancellationReason,
              addedNote: notes,
              today,
              recorded: recordedOutcome,
            }),
          },
          status: "closed",
          endDate: today,
        },
      });
      // Both before the step change, so the confirmation renders once with the
      // cycle's data already held rather than reading a cycle that is on its way
      // out. See `justSaved` and `savedTransferDate`.
      justSaved.current = true;
      setSavedTransferDate(transferDate ?? null);
      setStep("confirmation");
    } catch {
      // Keep the details step open; the error banner renders from mutation state.
    }
  };

  const isLoading = updateCycle.isPending || completeCycle.isPending;
  const errorMessage =
    completeCycle.error instanceof Error
      ? completeCycle.error.message
      : updateCycle.error instanceof Error
        ? updateCycle.error.message
        : null;

  return (
    <Dialog
      visible={visible}
      onClose={step === "confirmation" ? handleDone : handleClose}
      layout="centered"
      closeButton="minimal"
      containerStyle={{ maxWidth: 440 }}
      testID="end-cycle-modal"
    >
      {step === "details" ? (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("homepage.review_concept.post_procedure.back")),
              "homepage.review_concept.post_procedure.back",
            );
            handleBack();
          }}
          accessibilityRole="button"
          accessibilityLabel={t("homepage.review_concept.post_procedure.back")}
          hitSlop={8}
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            width: 28,
            height: 28,
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <ChevronLeft size={20} color={colors.textLight} strokeWidth={2} />
        </Pressable>
      ) : null}
      {step === "pick" && showRecordedResultRecap ? (
        <RecordedResultRecapStep
          recordedOutcome={recordedOutcome}
          recordedOutcomeType={recordedOutcomeType}
          onConfirm={handleSave}
          onChooseOther={() => handleSelectOutcome("other")}
          errorMessage={errorMessage}
          isLoading={isLoading}
          canConfirm={!!targetCycleId}
          t={t}
        />
      ) : step === "pick" ? (
        <PickStep
          options={outcomeOptions}
          promptKey={promptKey}
          onSelectOutcome={handleSelectOutcome}
          notes={notes}
          setNotes={setNotes}
          errorMessage={errorMessage}
          isLoading={isLoading}
          /**
           * A specimen count that disagrees with the recorded result is worth
           * saying, and it is not a reason to make a cycle permanently
           * unclosable.
           *
           * It used to gate this button, and the dead end was real: the
           * mismatch can come from a specimen attached to a different cycle —
           * something she cannot repair from this dialog, since the only
           * affordance here adds *more* specimens and pushes the count further
           * out. A cycle in that state could never be ended at all.
           *
           * The warning above stays, and stays prominent. Ending the cycle is
           * her decision, available at any point; the record keeps both numbers
           * so the disagreement remains visible to her care team rather than
           * being resolved by a button she was not allowed to press.
           */
          canConfirm={
            !!targetCycleId && (!!preservationSummary || !!outcomeType)
          }
          onCancel={handleClose}
          onConfirm={handleSave}
          preservationSummary={preservationSummary}
          isPreservation={isPreservation}
          t={t}
        />
      ) : step === "details" ? (
        <DetailsStep
          outcomeType={outcomeType}
          cancellationReason={cancellationReason}
          setCancellationReason={setCancellationReason}
          notes={notes}
          setNotes={setNotes}
          errorMessage={errorMessage}
          isLoading={isLoading}
          onConfirm={handleSave}
          t={t}
        />
      ) : (
        <ConfirmationStep
          type={outcomeType}
          // Held at save time: the live prop is derived from the cycle this write
          // just closed, so it empties out from under the step.
          // Only the value held at save time. Falling back to the live prop put
          // the coupling back: both callers derive it from phases that empty out
          // during the close, and on Plan home the closed cycle leaves the
          // transition slot — which can re-resolve to *another* open cycle, so
          // this cycle's confirmation would date its scan and trimester from that
          // one's transfer.
          transferDate={savedTransferDate}
          onDone={handleDone}
          onOpenArticle={onOpenArticle}
        />
      )}
    </Dialog>
  );
}

// ── Pick step ───────────────────────────────────────────────

const notesInputStyle = {
  minHeight: 56,
  marginTop: 14,
  paddingVertical: 10,
  paddingHorizontal: 12,
  borderRadius: 10,
  borderWidth: 1,
  borderColor: "rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.4)",
  fontSize: 13,
  color: colors.text,
  marginBottom: 20,
  textAlignVertical: "top" as const,
};

function outcomeIcon(optionId: EndCycleOptionId) {
  switch (optionId) {
    case "pregnancy":
      return <Heart size={17} color={colors.textMid} />;
    case "no_pregnancy":
      return <CircleAlert size={17} color={colors.textMid} />;
    case "miscarriage":
      return <HandHeart size={17} color={colors.textMid} />;
    case "cancelled":
      return <CalendarIcon size={17} color={colors.textMid} />;
    case "completed":
      return <CheckCircle2 size={17} color={colors.textMid} />;
    default:
      return <ClipboardList size={17} color={colors.textMid} />;
  }
}

function recordedOutcomeLabelKey(
  outcomeType: Exclude<EndCycleOptionId, "completed"> | null,
): string {
  switch (outcomeType) {
    case "pregnancy":
      return "you.modals.end_cycle.outcome_pregnancy";
    case "no_pregnancy":
      return "you.modals.end_cycle.outcome_no_pregnancy";
    case "miscarriage":
      return "you.modals.end_cycle.outcome_miscarriage";
    case "cancelled":
      return "you.modals.end_cycle.outcome_cancelled";
    default:
      return "you.modals.end_cycle.outcome_other";
  }
}

function recordedResultStatusKey(
  status: CycleOutcome["resultStatus"] | undefined,
): string | null {
  switch (status) {
    case "positive":
      return "you.modals.end_cycle.recorded_result_positive_status";
    case "negative":
      return "you.modals.end_cycle.recorded_result_negative_status";
    case "uncertain":
      return "you.modals.end_cycle.recorded_result_uncertain_status";
    default:
      return null;
  }
}

function RecordedResultRecapStep({
  recordedOutcome,
  recordedOutcomeType,
  onConfirm,
  onChooseOther,
  errorMessage,
  isLoading,
  canConfirm,
  t,
}: {
  recordedOutcome: CycleOutcome | null;
  recordedOutcomeType: Exclude<EndCycleOptionId, "completed"> | null;
  onConfirm: () => void;
  onChooseOther: () => void;
  errorMessage: string | null;
  isLoading: boolean;
  canConfirm: boolean;
  t: (key: string) => string;
}) {
  const { locale } = useLocale();
  const statusKey = recordedResultStatusKey(recordedOutcome?.resultStatus);
  const notes = recordedOutcome?.notes?.trim() ?? "";

  return (
    <>
      <View style={{ alignItems: "center", gap: 8, marginBottom: 18 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={68}
          height={72}
          style={{ marginBottom: 2 }}
          testID="end-cycle-recorded-recap-illustration"
        />
        <DialogHeader
          title={t("you.modals.end_cycle.recorded_result_recap_title")}
          subtitle={t("you.modals.end_cycle.recorded_result_recap_prompt")}
          style={{ width: "100%" }}
          titleStyle={{ textAlign: "center", width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
      </View>

      <View
        style={{
          padding: 14,
          borderRadius: 14,
          backgroundColor: "rgba(255,255,255,0.5)",
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.08)",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "700",
            color: colors.textFaint,
            textTransform: "uppercase",
            letterSpacing: 0.8,
          }}
        >
          {t("you.modals.end_cycle.recorded_result_label")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 18, fontWeight: "700", color: colors.text }}
        >
          {t(recordedOutcomeLabelKey(recordedOutcomeType))}
        </Text>

        {recordedOutcome?.dateRecorded ? (
          <View style={{ gap: 2 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: colors.textFaint }}
            >
              {t("you.modals.end_cycle.recorded_result_date_label")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 13, color: colors.textMid }}
            >
              {formatLong(recordedOutcome.dateRecorded, locale)}
            </Text>
          </View>
        ) : null}

        {statusKey ? (
          <View style={{ gap: 2 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: colors.textFaint }}
            >
              {t("you.modals.end_cycle.recorded_result_status_label")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 13, color: colors.textMid }}
            >
              {t(statusKey)}
            </Text>
          </View>
        ) : null}

        {notes ? (
          <View style={{ gap: 2 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: colors.textFaint }}
            >
              {t("you.modals.end_cycle.recorded_result_notes_label")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 13, lineHeight: 18, color: colors.textMid }}
            >
              {notes}
            </Text>
          </View>
        ) : null}
      </View>

      {errorMessage ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            padding: 12,
            borderRadius: 10,
            backgroundColor: `${colors.error}12`,
            borderWidth: 1,
            borderColor: `${colors.error}30`,
            marginBottom: 14,
          }}
        >
          <AlertTriangle size={16} color={colors.error} />
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 12, color: colors.error }}
          >
            {errorMessage}
          </Text>
        </View>
      ) : null}

      <DialogPrimaryButton
        label={t("you.modals.end_cycle.close_with_recorded_result")}
        onPress={onConfirm}
        loading={isLoading}
        disabled={!canConfirm || isLoading}
        fullWidth
      />
      <View style={{ height: 12 }} />
      <ActionRowCard
        leading={outcomeIcon("other")}
        title={t("you.modals.end_cycle.outcome_other")}
        subtitle={t("you.modals.end_cycle.choose_different_outcome")}
        onPress={onChooseOther}
        action="end_cycle_choose_other_outcome"
        accessibilityLabel={`${t("you.modals.end_cycle.outcome_other")}. ${t("you.modals.end_cycle.choose_different_outcome")}`}
      />
    </>
  );
}

function PickStep({
  options,
  promptKey,
  onSelectOutcome,
  notes,
  setNotes,
  errorMessage,
  isLoading,
  canConfirm,
  onCancel,
  onConfirm,
  preservationSummary,
  isPreservation,
  t,
}: {
  options: EndCycleOption[];
  promptKey: string;
  onSelectOutcome: (v: OutcomeType) => void;
  notes: string;
  setNotes: (s: string) => void;
  errorMessage: string | null;
  isLoading: boolean;
  canConfirm: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  preservationSummary?: PreservationCycleSummary;
  isPreservation: boolean;
  t: (key: string) => string;
}) {
  const titleKey = isPreservation
    ? "care.complete_cycle.title"
    : "you.modals.end_cycle.title";
  const subtitleKey = isPreservation
    ? "care.complete_cycle.description"
    : promptKey;

  return (
    <>
      <View style={{ alignItems: "center", gap: 8, marginBottom: 18 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={68}
          height={72}
          style={{ marginBottom: 2 }}
          testID="end-cycle-picker-illustration"
        />
        <DialogHeader
          title={t(titleKey)}
          subtitle={t(subtitleKey)}
          style={{ width: "100%" }}
          titleStyle={{ textAlign: "center", width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
      </View>
      {isPreservation && preservationSummary ? (
        <PreservationRecap summary={preservationSummary} />
      ) : (
        <ScrollView
          style={{ maxHeight: 430 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 10 }}
        >
          {options.map((option) => (
            <ActionRowCard
              key={option.id}
              leading={outcomeIcon(option.id)}
              title={t(option.labelKey)}
              subtitle={t(option.descriptionKey)}
              onPress={() => onSelectOutcome(option.id)}
              action="end_cycle_outcome_option"
              accessibilityLabel={`${t(option.labelKey)}. ${t(option.descriptionKey)}`}
            />
          ))}
        </ScrollView>
      )}

      {preservationSummary ? (
        <TextInput
          placeholder={t("you.modals.end_cycle.notes_placeholder")}
          placeholderTextColor={colors.textFaint}
          value={notes}
          onChangeText={setNotes}
          multiline
          style={notesInputStyle}
        />
      ) : null}

      {errorMessage && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            padding: 12,
            borderRadius: 10,
            backgroundColor: `${colors.error}12`,
            borderWidth: 1,
            borderColor: `${colors.error}30`,
            marginBottom: 14,
          }}
        >
          <AlertTriangle size={16} color={colors.error} />
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 12, color: colors.error }}
          >
            {errorMessage}
          </Text>
        </View>
      )}

      {preservationSummary ? (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
          <SoftSecondaryButton
            label={t("you.modals.end_cycle.cancel")}
            onPress={onCancel}
            flex={1}
          />
          <DialogPrimaryButton
            label={t("you.modals.end_cycle.confirm")}
            onPress={onConfirm}
            loading={isLoading}
            disabled={!canConfirm}
            flex={1}
          />
        </View>
      ) : null}
    </>
  );
}

function DetailsStep({
  outcomeType,
  cancellationReason,
  setCancellationReason,
  notes,
  setNotes,
  errorMessage,
  isLoading,
  onConfirm,
  t,
}: {
  outcomeType: OutcomeType | null;
  cancellationReason: CancellationReason | null;
  setCancellationReason: (value: CancellationReason | null) => void;
  notes: string;
  setNotes: (value: string) => void;
  errorMessage: string | null;
  isLoading: boolean;
  onConfirm: () => void;
  t: (key: string) => string;
}) {
  const capturePress = usePressCapture("DetailsStep");
  const isCancelled = outcomeType === "cancelled";
  const isOther = outcomeType === "other";
  const titleKey = isCancelled
    ? "you.modals.end_cycle.cancelled_details_title"
    : isOther
      ? "you.modals.end_cycle.other_details_title"
      : "you.modals.end_cycle.outcome_details_title";
  const promptKey = isCancelled
    ? "you.modals.end_cycle.cancelled_details_prompt"
    : isOther
      ? "you.modals.end_cycle.other_details_prompt"
      : "you.modals.end_cycle.outcome_details_prompt";

  return (
    <>
      <View style={{ alignItems: "center", gap: 8, marginBottom: 18 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={68}
          height={72}
          style={{ marginBottom: 2 }}
          testID="end-cycle-details-illustration"
        />
        <DialogHeader
          title={t(titleKey)}
          subtitle={t(promptKey)}
          style={{ width: "100%" }}
          titleStyle={{ textAlign: "center", width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
      </View>

      {isCancelled ? (
        <View style={{ marginBottom: 14 }}>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {CANCELLATION_REASON_ORDER.map((value) => {
              const isSelected = cancellationReason === value;
              return (
                <Pressable
                  key={value}
                  onPress={() => {
                    capturePress(
                      pressLabel(t(CANCELLATION_REASON_KEYS[value])),
                      "cancellation_reason_option",
                    );
                    setCancellationReason(isSelected ? null : value);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={t(CANCELLATION_REASON_KEYS[value])}
                  accessibilityState={{ selected: isSelected }}
                  aria-selected={isSelected}
                  style={({ pressed }) => ({
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: isSelected
                      ? "rgba(0,0,0,0.18)"
                      : "rgba(0,0,0,0.08)",
                    backgroundColor: isSelected
                      ? "rgba(0,0,0,0.06)"
                      : pressed
                        ? "rgba(255,255,255,0.7)"
                        : "rgba(255,255,255,0.5)",
                  })}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: isSelected ? "600" : "500",
                      color: colors.textMid,
                    }}
                  >
                    {t(CANCELLATION_REASON_KEYS[value])}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}

      <TextInput
        placeholder={t("you.modals.end_cycle.notes_placeholder")}
        placeholderTextColor={colors.textFaint}
        value={notes}
        onChangeText={setNotes}
        multiline
        style={notesInputStyle}
      />

      {errorMessage ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            padding: 12,
            borderRadius: 10,
            backgroundColor: `${colors.error}12`,
            borderWidth: 1,
            borderColor: `${colors.error}30`,
            marginBottom: 14,
          }}
        >
          <AlertTriangle size={16} color={colors.error} />
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 12, color: colors.error }}
          >
            {errorMessage}
          </Text>
        </View>
      ) : null}

      <DialogPrimaryButton
        label={t("you.modals.end_cycle.confirm")}
        onPress={onConfirm}
        loading={isLoading}
        disabled={isLoading}
        fullWidth
      />
    </>
  );
}

function PreservationRecap({ summary }: { summary: PreservationCycleSummary }) {
  const t = useTranslations();
  const capturePress = usePressCapture("PreservationRecap");
  const { locale } = useLocale();
  const totalSpecimens = getStoredSpecimenCount(summary);
  const matchingStoredSpecimens = getStoredSpecimenCount(
    summary,
    expectedStoredSpecimenType(summary.outcomeCounts.kind),
  );
  const storedSpecimensMismatch = hasStoredSpecimenMismatch(summary);
  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    const date = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(date.getTime())) return iso;
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  };
  const outcomeFields: [keyof PreservationOutcomeCounts, string][] = [
    ["retrieved", "care.complete_cycle.retrieved_count"],
    ["mature", "care.complete_cycle.mature_count"],
    ...(summary.outcomeCounts.kind === "embryos"
      ? ([["fertilized", "care.complete_cycle.fertilized_count"]] as [
          keyof PreservationOutcomeCounts,
          string,
        ][])
      : []),
    [
      "frozen",
      summary.outcomeCounts.kind === "embryos"
        ? "care.complete_cycle.embryos_frozen_count"
        : summary.outcomeCounts.kind === "sperm"
          ? "care.complete_cycle.vials_frozen_count"
          : "care.complete_cycle.frozen_count",
    ],
  ];

  return (
    <ScrollView
      style={{ maxHeight: 330, marginBottom: 16 }}
      showsVerticalScrollIndicator={false}
    >
      <View
        style={{
          borderRadius: 12,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.08)",
          backgroundColor: "rgba(255,255,255,0.35)",
          padding: 14,
          gap: 12,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            color: colors.textFaint,
            textTransform: "uppercase",
            letterSpacing: 0.6,
          }}
        >
          {t("care.complete_cycle.summary_header")}
        </Text>
        <SummaryRow
          label={t("care.complete_cycle.treatment_label")}
          value={`${summary.treatmentName} #${summary.cycleNumber}`}
        />
        {summary.clinicName ? (
          <SummaryRow
            label={t("care.complete_cycle.clinic_label")}
            value={summary.clinicName}
          />
        ) : null}
        <SummaryRow
          label={t("care.complete_cycle.started_label")}
          value={formatDate(summary.startDate)}
        />

        <View style={{ gap: 8, marginTop: 4 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: colors.textFaint,
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            {t("care.complete_cycle.outcome_review_header")}
          </Text>
          {outcomeFields.map(([field, labelKey]) => (
            <SummaryRow
              key={field}
              label={t(labelKey)}
              value={
                summary.outcomeCounts[field] == null
                  ? t("care.complete_cycle.not_recorded")
                  : String(summary.outcomeCounts[field])
              }
            />
          ))}
        </View>

        {(summary.outcomeCounts.frozen != null &&
        summary.outcomeCounts.frozen > 0
          ? matchingStoredSpecimens
          : totalSpecimens) > 0 ? (
          <SummaryRow
            label={t("care.complete_cycle.stored_specimens_label")}
            value={String(
              summary.outcomeCounts.frozen != null &&
                summary.outcomeCounts.frozen > 0
                ? matchingStoredSpecimens
                : totalSpecimens,
            )}
          />
        ) : null}

        {summary.outcomeCounts.frozen === 0 && totalSpecimens === 0 ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 12, lineHeight: 17, color: colors.textMid }}
          >
            {t("care.complete_cycle.confirmed_no_stored_specimens")}
          </Text>
        ) : null}

        {storedSpecimensMismatch ? (
          <View
            style={{
              gap: 10,
              padding: 12,
              borderRadius: 10,
              backgroundColor: `${colors.warm}10`,
              borderWidth: 1,
              borderColor: `${colors.warm}25`,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, lineHeight: 17, color: colors.textMid }}
            >
              {/* A sperm cycle can reach here with no count recorded at all,
                  and `String(null)` put the word "null" in front of the
                  patient. Nothing to compare, so say that instead. */}
              {summary.outcomeCounts.frozen == null
                ? t("care.complete_cycle.no_frozen_count_recorded")
                : t("care.complete_cycle.stored_specimens_count_mismatch", {
                    params: {
                      expectedCount: String(summary.outcomeCounts.frozen),
                      storedCount: String(matchingStoredSpecimens),
                    },
                  })}
            </Text>
            {summary.onAddSpecimen ? (
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("care.complete_cycle.add_stored_specimens")),
                    "care.complete_cycle.add_stored_specimens",
                  );
                  summary.onAddSpecimen?.();
                }}
                style={{ alignSelf: "flex-start" }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    color: colors.warmDark,
                  }}
                >
                  {t("care.complete_cycle.add_stored_specimens")}
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={{ flexDirection: "row", justifyContent: "space-between", gap: 12 }}
    >
      <Text
        className="font-dm-sans"
        style={{ flex: 1, fontSize: 13, color: colors.textMid }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 13, fontWeight: "600", color: colors.text }}
      >
        {value}
      </Text>
    </View>
  );
}

// ── Confirmation step ───────────────────────────────────────

function ConfirmationStep({
  type,
  transferDate,
  onDone,
  onOpenArticle,
}: {
  type: OutcomeType | null;
  transferDate: string | null;
  onDone: () => void;
  onOpenArticle?: (articleId: string) => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const isPositive = type === "pregnancy";
  // A preservation or donation cycle that finished is neither a result nor a
  // loss. Sending it down the negative branch would tell someone who completed
  // egg freezing "we're so sorry".
  const isCompletion = type === "completed";

  return (
    <>
      {/* Illustration above a centred title, the same shape every other dialog
          in the app uses. */}
      <View style={{ alignItems: "center", gap: 8, marginBottom: 16 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={72}
          height={76}
          testID="end-cycle-confirmation-illustration"
        />
        <DialogTitleText style={{ textAlign: "center" }}>
          {isCompletion
            ? t("you.modals.end_cycle.confirm_completed_title")
            : isPositive
              ? t("care.beta_result.header_congrats")
              : t("care.beta_result.header_with_you")}
        </DialogTitleText>
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 460 }}
        contentContainerStyle={{ gap: 18, paddingBottom: 4 }}
      >
        {isCompletion ? (
          // No pregnancy timeline and no bereavement resources: neither belongs
          // on a cycle that finished as intended.
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 21,
              color: colors.textMid,
              textAlign: "center",
            }}
          >
            {t("you.modals.end_cycle.confirm_completed_body")}
          </Text>
        ) : isPositive ? (
          <PositiveBody transferDate={transferDate} locale={locale} />
        ) : (
          <NegativeCloseoutSupport onOpenArticle={onOpenArticle} />
        )}
        <DialogPrimaryButton
          label={t("care.beta_result.done")}
          onPress={onDone}
          fullWidth
        />
      </ScrollView>
    </>
  );
}

// ── Positive body ──────────────────────────────────────────

function addDays(iso: string, days: number): string | null {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatLong(iso: string | null, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

function PositiveBody({
  transferDate,
  locale,
}: {
  transferDate: string | null;
  locale: string;
}) {
  const t = useTranslations();
  const ultrasound = transferDate ? addDays(transferDate, 28) : null;
  const trimesterEnd = transferDate ? addDays(transferDate, 77) : null;
  const halfWay = transferDate ? addDays(transferDate, 126) : null;

  return (
    <>
      <View style={{ gap: 8 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 20,
            fontWeight: "600",
            color: colors.text,
            letterSpacing: -0.3,
            lineHeight: 26,
          }}
        >
          {t("care.beta_result.positive_headline")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            fontWeight: "300",
            color: colors.textMid,
            lineHeight: 20,
          }}
        >
          {t("care.beta_result.positive_subhead")}
        </Text>
      </View>

      {transferDate && (
        <View style={{ gap: 10 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "700",
              color: colors.textFaint,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            {t("care.beta_result.whats_next")}
          </Text>
          <View style={{ gap: 8 }}>
            <TimelineRow
              label={t("care.beta_result.milestone_first_ultrasound")}
              hint={t("care.beta_result.milestone_first_ultrasound_hint")}
              date={ultrasound}
              locale={locale}
              primary
            />
            <TimelineRow
              label={t("care.beta_result.milestone_first_trimester_end")}
              hint={t("care.beta_result.milestone_first_trimester_end_hint")}
              date={trimesterEnd}
              locale={locale}
            />
            <TimelineRow
              label={t("care.beta_result.milestone_half_way")}
              hint={t("care.beta_result.milestone_half_way_hint")}
              date={halfWay}
              locale={locale}
            />
          </View>
        </View>
      )}

      <View
        style={{
          padding: 12,
          borderRadius: 12,
          backgroundColor: "rgba(0,0,0,0.03)",
          gap: 4,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Heart size={13} color={colors.green} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "600",
              color: colors.textMid,
              letterSpacing: -0.1,
            }}
          >
            {t("care.beta_result.keep_cycle_title")}
          </Text>
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "400",
            color: colors.textFaint,
            lineHeight: 17,
          }}
        >
          {t("care.beta_result.keep_cycle_body")}
        </Text>
      </View>
    </>
  );
}

function TimelineRow({
  label,
  hint,
  date,
  locale,
  primary,
}: {
  label: string;
  hint: string;
  date: string | null;
  locale: string;
  primary?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        padding: 12,
        borderRadius: 12,
        backgroundColor: primary ? `${colors.green}10` : "rgba(0,0,0,0.025)",
        borderWidth: 1,
        borderColor: primary ? `${colors.green}25` : "rgba(0,0,0,0.05)",
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          backgroundColor: primary ? `${colors.green}18` : "rgba(0,0,0,0.05)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CalendarIcon
          size={15}
          color={primary ? colors.green : colors.textMid}
        />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: colors.text,
            letterSpacing: -0.1,
          }}
        >
          {label}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "400",
            color: colors.textFaint,
            marginTop: 1,
          }}
        >
          {hint}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: primary ? colors.green : colors.textMid,
        }}
      >
        {formatLong(date, locale)}
      </Text>
    </View>
  );
}

// ── Negative body ──────────────────────────────────────────
